// Shared logo upload/replace/remove control (models the brand-identity page's
// AssetUpload). Wired to the partner-brand endpoints via the WS-1 hooks, which
// already invalidate the partner-brands cache. Requires a persisted brand id:
// the edit page always has one; the wizard reaches this at step 2, after step 1
// created the (inactive) row. Accepts PNG/JPEG only; hands the updated brand
// back through onChange so the container can sync logoMode/path/url.
//
// THREE SLOTS, ONE CONTROL. The base logo is the fallback and is used on every
// background unless a background-specific variant is uploaded. A partner who
// only ever uploads the base behaves exactly as before — the variants are
// additive and independently optional.
//
// The slots are one control rather than three, and one control rather than a
// sibling component, because the fallback relationship is only legible when the
// three are adjacent: "on-dark is empty, so the base is what renders there" is a
// sentence about the SET. Both surfaces (the wizard's logo step and the edit
// page's Asset branch) mount this one component, so they cannot drift — the
// same reason the colors / contact / ownership field groups are shared.
//
// NAMING TRAP, and why the previews look the way they do: a variant is named for
// the BACKGROUND it is drawn ON, not for the artwork's own color, and the two
// are opposites (the on-light variant is normally the dark-inked mark). Each
// preview is rendered on its own target background, so a swapped pair is visible
// at a glance — a light mark uploaded to the on-light slot disappears into it.

import { useRef, useState } from "react";
import { ImageIcon, Trash2, Upload } from "lucide-react";
import type { PartnerBrand, PartnerLogoVariant } from "@/api/partnerBrands";
import { Button, Label } from "@/design-system/components/ui";
import { extractErrorMessage } from "@/api/client";
import {
  useDeletePartnerBrandLogo,
  useDeletePartnerBrandLogoVariant,
  useUploadPartnerBrandLogo,
  useUploadPartnerBrandLogoVariant,
} from "@/hooks/partners/usePartnerBrands";

const ACCEPT = "image/png,image/jpeg";

export interface PartnerBrandLogoControlProps {
  brandId: string;
  logoUrl: string | null;
  /** Optional variant drawn when the surface BEHIND the logo is light. */
  logoUrlOnLight?: string | null;
  /** Optional variant drawn when the surface behind the logo is dark. */
  logoUrlOnDark?: string | null;
  onChange: (brand: PartnerBrand) => void;
}

export function PartnerBrandLogoControl({
  brandId,
  logoUrl,
  logoUrlOnLight,
  logoUrlOnDark,
  onChange,
}: PartnerBrandLogoControlProps) {
  const upload = useUploadPartnerBrandLogo(brandId);
  const remove = useDeletePartnerBrandLogo(brandId);

  return (
    <div className="flex flex-col gap-space-3">
      <LogoSlot
        label="Logo asset"
        testId="logo-slot-base"
        hint="PNG or JPEG, up to 5 MB. Shown in place of the wordmark on the funnel, and used on every background unless you add a variant below."
        previewUrl={logoUrl}
        surface="neutral"
        onFile={(file) =>
          upload.mutateAsync(file).then((brand) => {
            onChange(brand);
          })
        }
        onRemove={() =>
          remove.mutateAsync().then((brand) => {
            onChange(brand);
          })
        }
        uploading={upload.isPending}
        removing={remove.isPending}
      />

      <div>
        <Label className="mb-space-1 block">Background-specific versions (optional)</Label>
        <p className="text-caption text-gray-mid mt-0 mb-space-2 max-w-reading">
          Each slot is named for the background the logo sits ON, not for the color of the
          logo itself — they are usually opposites. Leave a slot empty and the logo above is
          used on that background. Check the preview: if the mark vanishes into its tile, the
          two are swapped.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-3">
          <VariantSlot
            brandId={brandId}
            variant="on-light"
            label="On light backgrounds"
            hint="Drawn on a light surface — usually the dark version of the mark."
            previewUrl={logoUrlOnLight ?? null}
            onChange={onChange}
          />
          <VariantSlot
            brandId={brandId}
            variant="on-dark"
            label="On dark backgrounds"
            hint="Drawn on a dark surface — usually the light or reversed version of the mark."
            previewUrl={logoUrlOnDark ?? null}
            onChange={onChange}
          />
        </div>
      </div>
    </div>
  );
}

function VariantSlot({
  brandId,
  variant,
  label,
  hint,
  previewUrl,
  onChange,
}: {
  brandId: string;
  variant: PartnerLogoVariant;
  label: string;
  hint: string;
  previewUrl: string | null;
  onChange: (brand: PartnerBrand) => void;
}) {
  const upload = useUploadPartnerBrandLogoVariant(brandId, variant);
  const remove = useDeletePartnerBrandLogoVariant(brandId, variant);

  return (
    <LogoSlot
      label={label}
      testId={`logo-slot-${variant}`}
      hint={hint}
      previewUrl={previewUrl}
      surface={variant === "on-light" ? "light" : "dark"}
      onFile={(file) =>
        upload.mutateAsync(file).then((brand) => {
          onChange(brand);
        })
      }
      onRemove={() =>
        remove.mutateAsync().then((brand) => {
          onChange(brand);
        })
      }
      uploading={upload.isPending}
      removing={remove.isPending}
    />
  );
}

/** Presentational — one upload/replace/remove row. The preview tile is rendered
 *  on the surface the slot is FOR, which is what makes a swapped pair obvious. */
function LogoSlot({
  label,
  testId,
  hint,
  previewUrl,
  surface,
  onFile,
  onRemove,
  uploading,
  removing,
}: {
  label: string;
  testId: string;
  hint: string;
  previewUrl: string | null;
  surface: "neutral" | "light" | "dark";
  onFile: (file: File) => Promise<unknown>;
  onRemove: () => Promise<unknown>;
  uploading: boolean;
  removing: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const run = (action: () => Promise<unknown>) => {
    setError(null);
    action().catch((e) => setError(extractErrorMessage(e)));
  };

  const tile =
    surface === "dark"
      ? "bg-onyx border-onyx"
      : surface === "light"
        ? "bg-white-pure border-border-light"
        : "bg-white-pure border-border-light";

  return (
    <div data-testid={testId}>
      <Label className="mb-space-2 block">{label}</Label>
      <div className="flex items-start gap-space-3 rounded-md border border-dashed border-border-light p-space-3 bg-white-warm">
        <div
          className={`h-16 w-16 shrink-0 rounded-md border flex items-center justify-center overflow-hidden ${tile}`}
        >
          {previewUrl ? (
            <img
              src={previewUrl}
              alt={`${label} preview`}
              className="h-full w-full object-contain"
            />
          ) : (
            <ImageIcon
              size={20}
              strokeWidth={1.5}
              aria-hidden
              className={surface === "dark" ? "text-white-warm" : "text-gray-mid"}
            />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-small text-onyx truncate">
            {previewUrl ? (
              "Logo uploaded"
            ) : (
              <span className="text-gray-mid italic">No logo uploaded</span>
            )}
          </div>
          <p className="text-caption text-gray-mid mt-space-1">{hint}</p>
          <div className="flex flex-wrap items-center gap-space-2 mt-space-2">
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT}
              className="hidden"
              aria-label={`${label} file`}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) run(() => onFile(f));
                e.target.value = "";
              }}
            />
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => inputRef.current?.click()}
              loading={uploading}
            >
              <Upload size={14} strokeWidth={1.5} aria-hidden />
              {previewUrl ? "Replace" : "Upload"}
            </Button>
            {previewUrl && (
              <Button
                type="button"
                size="sm"
                variant="text"
                onClick={() => run(onRemove)}
                loading={removing}
              >
                <Trash2 size={14} strokeWidth={1.5} aria-hidden /> Remove
              </Button>
            )}
          </div>
          {error && (
            <div className="mt-space-2 text-caption text-status-error" role="alert">
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
